import React, { Fragment } from "react";
import { Row, Col, Button, Card } from "reactstrap";
import ReactTooltip from "react-tooltip";
import Dotdotdot from "react-dotdotdot";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import notFound from "../../../media/img/not_found.png";
import {
  serviceCaller,
  withModals,
  withLoginRedirect,
  ServiceCallerProps,
} from "../../composition";
import removeMd from "remove-markdown";
import {
  getParsedDate,
  getPostThumbnailDisplayUrl,
  getTargetedDonors,
  getVideoMediaKey,
  postHasCustomThumbnail,
  postOnlySMS,
  smsChannelLabel,
  nodeForPostType,
} from "../../../utils/helpers";
import firebase from "firebase/compat/app";
import { postConsoleUrl } from "../../../utils/firebaseConsoleUrls";
import {
  IPost,
  PostType,
  deletableStatus,
  editableStatus,
} from "../../../types/services/IPost";
import IUser from "../../../types/services/IUser";
import { clonePost } from "../../../utils/fetch";
import { deletePostWithChildCleanup } from "../../../utils/postDelete";
import {
  isCurrentUserCollaborator,
  isCurrentUserReader,
} from "../../../utils/roleCheck";
import ISegment from "../../../types/services/ISegment";
import IClonePostResponse from "../../../types/services/IClonePostResponse";
import PostNotTargetedMetrics from "../postNotTargetedMetrics";
import SpecificContactsLabel from "../specificContactsLabel";

const IN_FLIGHT_SUPPORT_TOOLTIP_AFTER_MS = 60 * 60 * 1000;

function shouldShowInFlightSupportTooltip(post: IPost): boolean {
  const anchorMs = post.modifiedOn != null ? post.modifiedOn : post.createdOn;
  if (anchorMs == null) return false;
  return Date.now() - anchorMs >= IN_FLIGHT_SUPPORT_TOOLTIP_AFTER_MS;
}

interface Props extends ServiceCallerProps {
  isAdminDebugUi?: boolean;
  post: IPost;
  postType: PostType;
  index: number;
  user: {
    key: string;
    value: IUser;
  };
  segments: Array<ISegment>;
  excludeSegments: Array<ISegment>;
  deletedSegments: number;
  totalDonors: number;
  whitelabelUrl?: string;
  enableOnlySmsPost?: boolean;
  mediaUpdating?: boolean;

  togglePostNotificationModal: (post: IPost) => void;
}

interface State {
  loading: boolean;
}

class PostItem extends React.PureComponent<Props, State> {
  private inFlightSupportTooltipTimeoutId: ReturnType<
    typeof setTimeout
  > | null = null;

  state = {
    loading: false,
  };

  componentDidMount() {
    this.scheduleInFlightSupportTooltipReveal();
  }

  componentDidUpdate(prevProps: Props) {
    const p = this.props.post;
    const o = prevProps.post;
    if (
      p.id !== o.id ||
      p.status !== o.status ||
      p.modifiedOn !== o.modifiedOn ||
      p.createdOn !== o.createdOn
    ) {
      this.scheduleInFlightSupportTooltipReveal();
    }
  }

  componentWillUnmount() {
    this.clearInFlightSupportTooltipTimeout();
  }

  /** Whether notification-service is still working through this post's packages. */
  isSending = (): boolean => {
    const { post } = this.props;
    if (post.status === "delivering") return true;
    const progress = post.sendProgress;
    return (
      post.status === "delivered" &&
      !!progress &&
      typeof progress.expected === "number" &&
      (progress.done || 0) < progress.expected
    );
  };

  private clearInFlightSupportTooltipTimeout() {
    if (this.inFlightSupportTooltipTimeoutId != null) {
      clearTimeout(this.inFlightSupportTooltipTimeoutId);
      this.inFlightSupportTooltipTimeoutId = null;
    }
  }

  private scheduleInFlightSupportTooltipReveal() {
    this.clearInFlightSupportTooltipTimeout();
    const { post } = this.props;
    const inFlight =
      post.status === "uploading" || post.status === "processing";
    if (!inFlight) return;
    const anchorMs = post.modifiedOn != null ? post.modifiedOn : post.createdOn;
    if (anchorMs == null) return;
    const elapsed = Date.now() - anchorMs;
    if (elapsed >= IN_FLIGHT_SUPPORT_TOOLTIP_AFTER_MS) return;
    const delay = IN_FLIGHT_SUPPORT_TOOLTIP_AFTER_MS - elapsed;
    this.inFlightSupportTooltipTimeoutId = setTimeout(() => {
      this.inFlightSupportTooltipTimeoutId = null;
      this.forceUpdate();
      setTimeout(() => {
        ReactTooltip.rebuild();
      }, 0);
    }, delay);
  }

  getMediaUrl(post: IPost): string {
    return getPostThumbnailDisplayUrl(post);
  }

  editPost() {
    this.props.navigate(
      `/viewsparks/${this.props.postType}/${this.props.post.id}`
    );
  }

  clonePost = () => {
    if (window.confirm("Are you sure you want to clone this ViewSpark?")) {
      this.setState(
        {
          loading: true,
        },
        () => {
          clonePost(
            this.props.currentCharity.id,
            this.props.postType,
            this.props.post.id,
            this.props.config.adminServiceUrl,
            this.props.serviceAccessToken
          ).then(
            (clonePostResponse: IClonePostResponse) => {
              this.setState({
                loading: false,
              });
              window.alert(clonePostResponse.message);
              this.props.navigate(
                `/viewsparks/pending/${clonePostResponse.postId}`
              );
            },
            error => {
              console.error("Error cloning post", error);
              this.setState({
                loading: false,
              });
            }
          );
        }
      );
    }
  };

  deletePost = () => {
    if (window.confirm("Are you sure you want to delete this ViewSpark?")) {
      this.setState(
        {
          loading: true,
        },
        () => {
          deletePostWithChildCleanup(
            this.props.currentCharity.id,
            this.props.postType,
            this.props.post.id,
            this.props.config.adminServiceUrl,
            this.props.serviceAccessToken
          ).then(
            () => {
              this.setState({ loading: false });
            },
            error => {
              console.error("Error deleting post", error);
              window.alert(
                "Could not delete this ViewSpark. Check the console and try again."
              );
              this.setState({
                loading: false,
              });
            }
          );
        }
      );
    }
  };

  cancelDelivery = () => {
    if (
      !window.confirm(
        "Cancel this ViewSpark? Contacts not yet reached will not receive it."
      )
    ) {
      return;
    }
    this.setState({ loading: true }, () => {
      firebase
        .database()
        .ref(
          `${this.props.currentCharity.id}/publishedPosts/${this.props.post.id}/status`
        )
        .set("cancelled")
        .catch(error => {
          console.error("Error cancelling post", error);
          window.alert(
            "Could not cancel this ViewSpark. Check the console and try again."
          );
        })
        .finally(() => this.setState({ loading: false }));
    });
  };

  hasEditStatus = () => {
    return !this.props.post || editableStatus.includes(this.props.post.status);
  };

  getSegmentsMap = (SegmentsList: Array<ISegment>) => {
    return SegmentsList.map(seg => {
      if (this.props.postType === "pending") {
        return (
          <span key={seg.id}>
            {seg.isKeyword ? `Keyword - ${seg.name}` : seg.name}
            <br />
          </span>
        );
      } else {
        return (
          <span key={seg.id}>
            {seg.name +
              ": " +
              (this.props.post.segmentsCount &&
              this.props.post.segmentsCount[seg.id] >= 0
                ? this.props.post.segmentsCount[seg.id]
                : "unavailable")}
            <br />
          </span>
        );
      }
    });
  };

  getSegmentCountOrUnavailable = (seg: ISegment): number | "unavailable" => {
    if (this.props.postType === "pending") {
      return "unavailable";
    }
    const sc = this.props.post.segmentsCount;
    if (!sc || seg.id == null) {
      return "unavailable";
    }
    const v = sc[seg.id];
    if (typeof v !== "number" || v < 0) {
      return "unavailable";
    }
    return v;
  };

  sumSegmentList = (list: Array<ISegment>): number | null => {
    if (this.props.postType === "pending") {
      return null;
    }
    let sum = 0;
    for (const seg of list) {
      const v = this.getSegmentCountOrUnavailable(seg);
      if (v === "unavailable") {
        return null;
      }
      sum += v;
    }
    return sum;
  };

  donorMapCount = (map?: { [key: string]: boolean }): number => {
    return map ? Object.keys(map).length : 0;
  };

  renderIncludedTotalLine = () => {
    if (this.props.postType === "pending" || this.props.segments.length === 0) {
      return null;
    }
    const included = this.sumSegmentList(this.props.segments);
    const total =
      included === null
        ? null
        : included + this.donorMapCount(this.props.post.donors);
    const fmt = (n: number | null) => (n === null ? "—" : String(n));
    return (
      <p className={s.date}>
        <strong>Included total:</strong> {fmt(total)}
      </p>
    );
  };

  renderExcludedTotalLine = () => {
    if (
      this.props.postType === "pending" ||
      this.props.excludeSegments.length === 0
    ) {
      return null;
    }
    const excluded = this.sumSegmentList(this.props.excludeSegments);
    const total =
      excluded === null
        ? null
        : excluded + this.donorMapCount(this.props.post.excludeDonors);
    const fmt = (n: number | null) => (n === null ? "—" : String(n));
    return (
      <p className={s.date}>
        <strong>Excluded total:</strong> {fmt(total)}
      </p>
    );
  };

  renderTotalSelectedLine = () => {
    const selected = this.getListTotalSelected();
    if (selected === null) {
      return null;
    }

    const idx = this.props.index;

    return (
      <p className={s.date}>
        <strong>Total selected:</strong> {selected}{" "}
        <span data-tip data-for={`tooltip-total-selected-${idx}`}>
          <i className={`fa fa-question-circle ${s.helpIcon}`} />
        </span>
        <ReactTooltip
          id={`tooltip-total-selected-${idx}`}
          type="info"
          place="bottom"
          effect="float"
          delayHide={500}
        >
          Preliminary estimate; final audience is often lower after validation,
          deduplication, and removing invalid contacts.
        </ReactTooltip>
      </p>
    );
  };

  getListTotalSelected = (): number | null => {
    const { segments, excludeSegments, postType } = this.props;
    if (postType === "pending") {
      return null;
    }
    if (segments.length === 0 && excludeSegments.length === 0) {
      return null;
    }
    const included = this.sumSegmentList(segments);
    const excluded = this.sumSegmentList(excludeSegments);
    if (segments.length === 0 && excludeSegments.length > 0) {
      return null;
    }
    if (included === null || excluded === null) {
      return null;
    }
    return (
      included +
      this.donorMapCount(this.props.post.donors) -
      (excluded + this.donorMapCount(this.props.post.excludeDonors))
    );
  };

  getCounts = () => {
    const smsLabel = smsChannelLabel(this.props.post.postType);
    const counts = this.props.post.notificationsCount;
    const notTargeted = (
      <PostNotTargetedMetrics
        lineClassName={s.date}
        notTargeted={counts?.notTargeted}
        charityId={this.props.currentCharity?.id}
        postId={this.props.post.id}
        searchUrl={this.props.config?.searchServiceUrl}
        token={this.props.serviceAccessToken}
        tooltipId={`post-item-${this.props.post.id || this.props.index}`}
      />
    );
    return (
      <Fragment>
        <p className={s.date}>
          {`${smsLabel}: ${this.channelCount("sms", counts?.sms)}`}
        </p>
        <p className={s.date}>
          {`Email: ${this.channelCount("email", counts?.email)}`}
        </p>
        {notTargeted}
      </Fragment>
    );
  };

  channelCount = (
    channel: "sms" | "email",
    count: number | undefined
  ): string | number => {
    const { post } = this.props;
    if (post.notifications && post.notifications[channel] === false) {
      return "Off";
    }
    if (typeof count === "number") return count;
    return this.isSending() || this.acceptedCountPending()
      ? "Sending…"
      : 0;
  };

  /** Whether a delivering or delivered post with SMS or email enabled and a non-empty audience has no accepted count yet. */
  acceptedCountPending = (): boolean => {
    const { post } = this.props;
    const noCounts =
      typeof post.notificationsCount?.sms !== "number" &&
      typeof post.notificationsCount?.email !== "number";
    const channelEnabled =
      !post.disableNotifications &&
      (post.notifications?.sms !== false ||
        post.notifications?.email !== false);
    return (
      noCounts &&
      channelEnabled &&
      (post.status === "delivering" ||
        (post.status === "delivered" && (post.targetedDonors || 0) > 0))
    );
  };

  getStatus = (
    postStatus: string,
    isEmbeddedPost: boolean = false,
    isPaused: boolean = false
  ): string => {
    if ((postStatus == "ready" || postStatus == "delivering") && isPaused) return "Waiting for contacts to finish processing to deliver";
    if (isEmbeddedPost) {
      if (postStatus == "delivering") return "Publishing";
      if (postStatus == "delivered") return "Published";
    }

    if (postStatus == "onhold") return "Scheduled";
    return postStatus
      ? postStatus.charAt(0).toUpperCase() + postStatus.slice(1)
      : "Not available";
  };

  render() {
    const { post, user } = this.props;
    let mediaUrl = this.getMediaUrl(post);

    const titleTrimmed =
      post.title != null ? String(post.title).trim() : "";
    const contentForList = removeMd(post.content || "");
    const contentTrimmedForList = contentForList.trim();

    const postIsInFlight =
      post.status === "uploading" || post.status === "processing";
    const mediaUpdating = Boolean(this.props.mediaUpdating);
    const actionsLocked = postIsInFlight || mediaUpdating;
    const inFlightTooltipId = `post-in-flight-${
      post.id != null ? post.id : this.props.index
    }`;
    const showInFlightSupportTooltip =
      postIsInFlight && shouldShowInFlightSupportTooltip(post);

    const showTargetedContacts =
      post.status === "delivering" ||
      (post.targetedDonors !== null &&
        typeof post.targetedDonors !== "undefined" &&
        post.targetedDonors >= 0);
    const acceptedCountPending = this.acceptedCountPending();

    const listMetricsEligible =
      !!post.notificationsCount ||
      (post.notifications &&
        !post.notifications.email &&
        !post.notifications.sms &&
        !post.notifications.push) ||
      showTargetedContacts;

    const postIsDelivering = post.status === "delivering";
    const showMetricsColumn =
      (post.status === "delivered" || postIsDelivering) && listMetricsEligible;

    const specificDonors =
      this.props.post.donors && Object.keys(this.props.post.donors).length > 0;
    const excludedDonors =
      this.props.post.excludeDonors &&
      Object.keys(this.props.post.excludeDonors).length > 0;
    const excludedSegments =
      this.props.excludeSegments && this.props.excludeSegments.length > 0;

    const tooltipKey =
      post.id != null ? String(post.id) : `idx-${this.props.index}`;
    const hasVideo = Boolean(post.media && getVideoMediaKey(post.media));
    const hasCustomThumbnail = postHasCustomThumbnail(post);
    const customThumbTooltipId = `post-custom-thumb-${tooltipKey}`;

    return (
      <Card className={s.card}>
        <Row data-elm-id={`post_list_row_${this.props.index}`}>
          <Col xs={4} md={1} className={s.colMargin}>
            <div className={s["img-container"]}>
              <img
                className={s.img}
                data-elm-id="img_logo"
                src={mediaUrl !== "" ? mediaUrl : notFound}
                alt=""
              />
              {hasVideo && (
                <i
                  className={`fa fa-play-circle ${s.thumbPlay}`}
                  aria-hidden="true"
                />
              )}
              {hasCustomThumbnail && (
                <>
                  <span
                    className={s.thumbCustom}
                    data-tip
                    data-for={customThumbTooltipId}
                  >
                    <i className="fa fa-camera" aria-hidden="true" />
                  </span>
                  <ReactTooltip
                    id={customThumbTooltipId}
                    type="info"
                    place="top"
                    effect="float"
                  >
                    Custom thumbnail
                  </ReactTooltip>
                </>
              )}
              {mediaUpdating && (
                <div
                  className={s.thumbUpdating}
                  role="status"
                  aria-busy="true"
                  aria-label="Updating ViewSpark"
                >
                  <i className="fa fa-spinner fa-spin" aria-hidden />
                </div>
              )}
            </div>
          </Col>
          <Col xs={8} md={3} className={s.colMargin}>
            <div className={s.content}>
              <h6 data-elm-id={"post_title_" + this.props.index}>
                {titleTrimmed ? (
                  titleTrimmed
                ) : postOnlySMS(post.postType) ? (
                  "Only SMS post"
                ) : (
                  <Fragment>
                    Untitled
                    <span
                      className={s.placeholderTip}
                      data-tip
                      data-for={`tooltip-post-no-title-${tooltipKey}`}
                    >
                      <i
                        className={`fa fa-question-circle ${s.helpIcon}`}
                        aria-label="About missing title"
                      />
                    </span>
                    <ReactTooltip
                      id={`tooltip-post-no-title-${tooltipKey}`}
                      type="info"
                      place="top"
                      effect="float"
                      delayHide={500}
                    >
                      This post currently has no title
                    </ReactTooltip>
                  </Fragment>
                )}
              </h6>
              <Dotdotdot clamp={2}>
                <p data-elm-id={"post_content_" + this.props.index}>
                  {contentTrimmedForList ? (
                    contentForList
                  ) : (
                    <Fragment>
                      No content
                      <span
                        className={s.placeholderTip}
                        data-tip
                        data-for={`tooltip-post-no-content-${tooltipKey}`}
                      >
                        <i
                          className={`fa fa-question-circle ${s.helpIcon}`}
                          aria-label="About missing content"
                        />
                      </span>
                      <ReactTooltip
                        id={`tooltip-post-no-content-${tooltipKey}`}
                        type="info"
                        place="top"
                        effect="float"
                        delayHide={500}
                      >
                        This post currently has no content
                      </ReactTooltip>
                    </Fragment>
                  )}
                </p>
              </Dotdotdot>
            </div>
          </Col>
          <Col xs={12} md={2} className={s.colMargin}>
            {post.status === "error" ? (
              <Fragment>
                <p
                  data-elm-id={`post_status_${this.props.index}`}
                  className={`${s.date} ${s.failedToSend}`}
                >
                  <strong>Failed to send</strong>
                </p>
                {post.errorMessage ? (
                  <p className={s.date}>
                    <strong>Reason:</strong> {post.errorMessage}
                  </p>
                ) : null}
              </Fragment>
            ) : (
              <p
                data-elm-id={`post_status_${this.props.index}`}
                className={s.date}
              >
                {mediaUpdating ? (
                  <strong>Updating ViewSpark</strong>
                ) : (
                  <Fragment>
                    <strong>Status:</strong>{" "}
                    {this.getStatus(
                      post.status,
                      post.isEmbeddedPost,
                      post.isPaused === true
                    )}
                  </Fragment>
                )}
              </p>
            )}
            {this.props.isAdminDebugUi &&
              post.id &&
              (firebase.app().options as { projectId?: string }).projectId && (
                <p className={s.date}>
                  <strong>Post ID:</strong>{" "}
                  <a
                    href={postConsoleUrl(
                      (firebase.app().options as { projectId?: string })
                        .projectId as string,
                      this.props.currentCharity.id,
                      nodeForPostType(this.props.postType) as
                        | "pendingPosts"
                        | "publishedPosts",
                      post.id
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {post.id}
                  </a>
                </p>
              )}
            <p className={s.date}>
              <strong>Author:</strong>{" "}
              {user ? user.value.name : "Not available"}
            </p>
            {!post.isEmbeddedPost && (
              <Fragment>
                {this.props.post.motivationCode !== null &&
                  typeof this.props.post.motivationCode !== "undefined" &&
                  this.props.post.motivationCode !== "" && (
                    <Fragment>
                      <p className={s.date}>
                        {"Motivation Code: " + this.props.post.motivationCode}
                      </p>
                    </Fragment>
                  )}
                {specificDonors && this.props.segments.length === 0 && (
                  <SpecificContactsLabel
                    lineClassName={s.date}
                    donorIds={Object.keys(this.props.post.donors || {})}
                    charityId={this.props.currentCharity?.id}
                    searchUrl={this.props.config?.searchServiceUrl}
                    token={this.props.serviceAccessToken}
                    tooltipId={`specific-contacts-${this.props.post.id ||
                      this.props.index}`}
                  />
                )}
                {excludedDonors &&
                  this.props.excludeSegments.length === 0 && (
                    <SpecificContactsLabel
                      lineClassName={s.date}
                      donorIds={Object.keys(this.props.post.excludeDonors || {})}
                      charityId={this.props.currentCharity?.id}
                      searchUrl={this.props.config?.searchServiceUrl}
                      token={this.props.serviceAccessToken}
                      tooltipId={`excluded-contacts-${this.props.post.id ||
                        this.props.index}`}
                      label="Excluded Contacts"
                    />
                  )}
                {this.props.segments.length > 0 && (
                  <Fragment>
                    <p className={s.date}>
                      <strong>Included Segments:</strong> <br />
                      {this.getSegmentsMap(this.props.segments)}
                      {specificDonors && (
                        <SpecificContactsLabel
                          donorIds={Object.keys(this.props.post.donors || {})}
                          charityId={this.props.currentCharity?.id}
                          searchUrl={this.props.config?.searchServiceUrl}
                          token={this.props.serviceAccessToken}
                          tooltipId={`specific-contacts-${this.props.post.id ||
                            this.props.index}`}
                          inline
                        />
                      )}
                    </p>
                    {this.renderIncludedTotalLine()}
                  </Fragment>
                )}
                {excludedSegments && (
                  <Fragment>
                    <p className={s.date}>
                      <strong>Excluded Segments:</strong> <br />
                      {this.getSegmentsMap(this.props.excludeSegments)}
                      {excludedDonors && (
                        <SpecificContactsLabel
                          donorIds={Object.keys(
                            this.props.post.excludeDonors || {}
                          )}
                          charityId={this.props.currentCharity?.id}
                          searchUrl={this.props.config?.searchServiceUrl}
                          token={this.props.serviceAccessToken}
                          tooltipId={`excluded-contacts-${this.props.post.id ||
                            this.props.index}`}
                          inline
                        />
                      )}
                    </p>
                    {this.renderExcludedTotalLine()}
                  </Fragment>
                )}
                {this.renderTotalSelectedLine()}
                {!this.props.post.isParentPost &&
                  !this.props.post.donors &&
                  !this.props.post.excludeDonors &&
                  this.props.segments.length === 0 &&
                  this.props.excludeSegments.length === 0 && (
                    <p className={s.date}>All Contacts</p>
                  )}
                {!this.props.post.isParentPost &&
                  !this.props.post.donors &&
                  !this.props.post.excludeDonors &&
                  this.props.segments.length === 0 &&
                  this.props.excludeSegments.length > 0 &&
                  this.props.totalDonors === this.props.post.targetedDonors && (
                    <Fragment>
                      <p className={s.date}>0 Records to exclude</p>
                      <p className={s.date}>All Contacts</p>
                    </Fragment>
                  )}

                {this.props.post.isParentPost && (
                  <p className={s.date}>To selected sub-accounts</p>
                )}

                {this.props.deletedSegments > 0 && (
                  <Fragment>
                    <p className={s.date}>
                      <span>
                        {"Deleted Segments: " +
                          this.props.deletedSegments +
                          " "}
                        <span
                          data-tip="tooltip-deleted-segments"
                          data-for="tooltip-deleted-segments"
                        >
                          <i className={`fa fa-question-circle ${s.helpIcon}`} />
                        </span>
                        <br />
                      </span>
                    </p>
                    <ReactTooltip
                      id="tooltip-deleted-segments"
                      type="info"
                      place="right"
                      effect="float"
                      delayHide={500}
                    >
                      Some included or excluded segments for this post were
                      deleted after publishing
                    </ReactTooltip>
                  </Fragment>
                )}
              </Fragment>
            )}
            {post.isEmbeddedPost && post.status == "draft" && (
              <Fragment>
                <br />
                <p className={s.date}>{"To publish to site"}</p>
              </Fragment>
            )}
            {post.isEmbeddedPost && post.status != "draft" && (
              <Fragment>
                <br />
                <p className={s.date}>{"Published to site"}</p>
              </Fragment>
            )}
          </Col>
          {showMetricsColumn && (
            <Col xs={12} md={2} className={s.colMargin}>
              {!post.isEmbeddedPost && (
                <Fragment>
                  {showTargetedContacts && (
                    <p className={s.date}>
                      <strong>Targeted Contacts:</strong>{" "}
                      {acceptedCountPending
                        ? "Sending…"
                        : getTargetedDonors(this.props.post)}{" "}
                      <span
                        data-tip
                        data-for={`tooltip-targeted-contacts-${this.props.index}`}
                      >
                        <i className={`fa fa-question-circle ${s.helpIcon}`} />
                      </span>
                      <ReactTooltip
                        id={`tooltip-targeted-contacts-${this.props.index}`}
                        type="info"
                        place="right"
                        effect="float"
                        delayHide={500}
                      >
                        This is the total number of contacts we sent this post.
                        It can differ from the total selected count.
                      </ReactTooltip>
                    </p>
                  )}
                  <p className={s.date}>
                    <strong>
                      {this.isSending()
                        ? "Target Metrics (sending, live)"
                        : "Target Metrics"}
                    </strong>
                  </p>
                  {this.getCounts()}
                </Fragment>
              )}
            </Col>
          )}
          <Col
            xs={12}
            md={{
              size: 3,
              offset: showMetricsColumn ? 0 : 2,
            }}
            className={s.colMargin}
          >
            <p className={s.date}>
              <strong>Created:</strong>{" "}
              {post.createdOn
                ? getParsedDate(
                    new Date(post.createdOn).toISOString(),
                    false,
                    "MM/DD/YYYY, hh:mm a"
                  )
                : "Not available"}
            </p>
            {post.status && ["delivered", "onhold"].includes(post.status) && (
              <p className={s.date}>
                <strong>
                  {post.status === "delivered"
                    ? "Published:"
                    : "Scheduled for:"}
                </strong>{" "}
                {post.scheduleTime
                  ? getParsedDate(
                      new Date(post.scheduleTime).toISOString(),
                      false,
                      "MM/DD/YYYY, hh:mm a"
                    )
                  : "Not available"}
              </p>
            )}
            {this.props.enableOnlySmsPost && (
              <p className={s.date}>
                <strong>Message Type:</strong>{" "}
                {smsChannelLabel(post.postType)}
              </p>
            )}
            {post.status && post.status === "delivered" && (
              <p className={s.date}>
                <a
                  href={`${this.props.whitelabelUrl}/post?id=${this.props.post.id}`}
                  target="_blank"
                >
                  ViewSpark URL
                </a>
              </p>
            )}
          </Col>

          <Col
            xs={12}
            md={1}
            className={
              actionsLocked ? `${s.colMargin} ${s.actionsCol}` : s.colMargin
            }
          >
            <div
              className={
                actionsLocked
                  ? `${s.buttonContainer} ${s.buttonContainerInFlight}`
                  : s.buttonContainer
              }
            >
              {actionsLocked ? (
                <Fragment>
                  {showInFlightSupportTooltip ? (
                    <span
                      className={s.inFlightSpinner}
                      data-tip
                      data-for={inFlightTooltipId}
                      role="status"
                      aria-busy="true"
                    >
                      <i className="fa fa-spinner fa-spin" aria-hidden />
                    </span>
                  ) : (
                    <span
                      className={s.inFlightSpinner}
                      role="status"
                      aria-busy="true"
                    >
                      <i className="fa fa-spinner fa-spin" aria-hidden />
                    </span>
                  )}
                  {showInFlightSupportTooltip && (
                    <ReactTooltip
                      id={inFlightTooltipId}
                      place="top"
                      effect="solid"
                      type="dark"
                    >
                      <span>Taking too long? Contact us for support</span>
                    </ReactTooltip>
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  <Button
                    id={"editPost_" + this.props.index}
                    className={s.button1}
                    color={"none"}
                    onClick={() => this.editPost()}
                    disabled={!this.hasEditStatus()}
                  >
                    {isCurrentUserCollaborator(this.props.currentCharity)
                      ? "Edit"
                      : "View"}
                  </Button>
                  {isCurrentUserCollaborator(this.props.currentCharity) && (
                    <Button
                      id={"deletePost_" + this.props.index}
                      className={s.button2}
                      color={"none"}
                      disabled={
                        this.state.loading ||
                        !deletableStatus.includes(post.status)
                      }
                      onClick={() => this.deletePost()}
                    >
                      {this.state.loading && (
                        <i className="fa fa-spinner fa-spin">&nbsp;</i>
                      )}
                      {!this.state.loading && <span>Delete</span>}
                    </Button>
                  )}
                  {isCurrentUserCollaborator(this.props.currentCharity) &&
                    (this.props.post.status === "delivered" ||
                      this.props.post.status === "error" ||
                      this.props.post.status === "cancelled") && (
                      <Button
                        id={"clonePost_" + this.props.index}
                        className={s.button2}
                        color={"none"}
                        disabled={
                          this.state.loading ||
                          !deletableStatus.includes(post.status)
                        }
                        onClick={() => this.clonePost()}
                      >
                        {this.state.loading && (
                          <i className="fa fa-spinner fa-spin">&nbsp;</i>
                        )}
                        {!this.state.loading && <span>Clone</span>}
                      </Button>
                    )}
                  {this.props.isAdminDebugUi &&
                    this.props.postType === "published" &&
                    this.isSending() && (
                      <Button
                        id={"cancelPost_" + this.props.index}
                        className={s.button2}
                        color={"none"}
                        disabled={this.state.loading}
                        onClick={this.cancelDelivery}
                      >
                        {this.state.loading && (
                          <i className="fa fa-spinner fa-spin">&nbsp;</i>
                        )}
                        {!this.state.loading && <span>Cancel</span>}
                      </Button>
                    )}
                  {isCurrentUserReader(this.props.currentCharity) &&
                    !post.isEmbeddedPost &&
                    !post.isParentPost &&
                    post.status === "delivered" && (
                      <Button
                        className={s.button3}
                        color={"none"}
                        disabled={this.state.loading}
                        onClick={() =>
                          this.props.togglePostNotificationModal(
                            this.props.post
                          )
                        }
                      >
                        {this.state.loading && (
                          <i className="fa fa-spinner fa-spin">&nbsp;</i>
                        )}
                        {!this.state.loading && <span>{" Metrics "}</span>}
                      </Button>
                    )}
                </Fragment>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    );
  }
}

export default serviceCaller(
  withLoginRedirect(withModals(withStyles(s)(PostItem)))
);
